import { BillingItem } from '@sportninja/common/hooks/useRegistrationDetails'

/**
 * The subset of a billing row these helpers actually read. Both `BillingItem`
 * (registration surface) and `BillingScheduleItem` (Team Billing surface) satisfy
 * it, so the helpers stay generic over the caller's row type — preserving extra
 * fields like `team_registration` that only one surface carries.
 */
type MinimalRow = {
  paid: boolean
  refunded: boolean
  paid_in_person?: boolean
  charge?: unknown
  payment_status?: 'processing' | null
  payment_type: string
  subtotal: number
}

/**
 * Payment types the "Pay Full Balance" consolidation collects. Mirrors the backend
 * `RegistrationBillingScheduleService::eligibleConsolidationRows()` `whereIn(...)` —
 * deposit, installment, an unpaid `full` row (manually-added or a deferred
 * pay-in-full), AND an unpaid `additional` row (SN-8310 Add Invoice: part of the
 * player's share, so paying the whole balance pays it too). `adhoc` stays excluded.
 * Keep this list in lockstep with the backend.
 */
export const PAY_IN_FULL_ELIGIBLE_TYPES = [
  'deposit',
  'installment',
  'full',
  'additional',
]

/**
 * Rows the pay-in-full consolidation would replace — a display-only mirror of the
 * server's eligibility (unpaid, not refunded, not paid in person, no charge history).
 * Drives the modal's "N scheduled payments" count/total; button *visibility* is a
 * separate predicate (`shouldShowPayInFullButton`) — eligibility ≠ visibility. The
 * endpoint recalculates authoritatively inside a transaction.
 *
 * Generic over the row type so the caller keeps its concrete type (and any extra
 * fields) in the returned array.
 */
export const getPayInFullEligibleRows = <T extends MinimalRow = BillingItem>(
  billing: T[] = []
): T[] =>
  billing.filter(
    (el) =>
      !el.paid &&
      !el.refunded &&
      !el.paid_in_person &&
      !el.charge &&
      // R8-#3: a claimed row (charge in flight) is not payable — the server would 409.
      el.payment_status !== 'processing' &&
      PAY_IN_FULL_ELIGIBLE_TYPES.includes(el.payment_type)
  )

/**
 * Whether to show the "Pay Full Balance" button. A single eligible row already has
 * its own per-row Pay button, so consolidation is only useful for 2+ scheduled
 * payments — hide the button below that threshold.
 */
export const shouldShowPayInFullButton = <T extends MinimalRow = BillingItem>(
  rows: T[]
): boolean => rows.length >= 2

/**
 * Pre-tax subtotal of the eligible rows — matches the billing header's "Total
 * Outstanding". The pre-payment modal shows this subtotal; tax is itemized and the
 * tax-inclusive total is shown on the /pay screen where the charge is taken.
 */
export const sumSubtotal = <T extends MinimalRow = BillingItem>(
  rows: T[]
): number => rows.reduce((sum, el) => sum + Number(el.subtotal ?? 0), 0)
